import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fetchData } from '../../apiService';
import { IconButton } from '@mui/material';
import { useAuth } from '../../AuthContext';
import { tokenIsExpired } from '../../authUtils';
import './Home.css';
import './HomeEnhanced.css';

import ShoppingCartIcon    from '@mui/icons-material/ShoppingCart';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ExitToAppIcon       from '@mui/icons-material/ExitToApp';
import AssessmentIcon      from '@mui/icons-material/Assessment';
import InventoryIcon       from '@mui/icons-material/Inventory';
import VisibilityIcon      from '@mui/icons-material/Visibility';
import InfoIcon            from '@mui/icons-material/Info';
import CopyrightIcon       from '@mui/icons-material/Copyright';
import BuildIcon           from '@mui/icons-material/Build';
import RefreshIcon         from '@mui/icons-material/Refresh';
import TrendingUpIcon      from '@mui/icons-material/TrendingUp';
import ReceiptLongIcon     from '@mui/icons-material/ReceiptLong';
import SwapHorizIcon       from '@mui/icons-material/SwapHoriz';
import Inventory2Icon      from '@mui/icons-material/Inventory2';
import PeopleAltIcon       from '@mui/icons-material/PeopleAlt';
import AddIcon             from '@mui/icons-material/Add';
import AsyncSelect         from 'react-select/async';

/* ── helpers ─────────────────────────────────────────────── */
const todayStr   = () => new Date().toISOString().split('T')[0];
const monthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };
const fmt   = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n ?? 0));
const fmtD  = (d) => new Date(d).toLocaleDateString('en-GB');
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/* ── Skeleton ────────────────────────────────────────────── */
const Skel = ({ w = '60%' }) => <div className="dash-skel" style={{ width: w }} />;

/* ── Top summary card ────────────────────────────────────── */
const SummaryCard = ({ icon, label, value, rows, color, loading, to }) => (
  <div className="dash-sum-card" style={{ '--sc': color }}>
    <div className="dash-sum-top">
      <div className="dash-sum-icon">{icon}</div>
      <div className="dash-sum-head">
        <div className="dash-sum-label">{label}</div>
        {loading ? <Skel w="50%" /> : <div className="dash-sum-value">{value}</div>}
      </div>
      {to && <Link to={to} className="dash-sum-link">→</Link>}
    </div>
    <div className="dash-sum-rows">
      {rows && rows.map((r, i) => (
        <div key={i} className="dash-sum-row">
          <span className="dash-sum-row-label">{r.label}</span>
          {loading ? <Skel w="28%" /> : (
            <span className="dash-sum-row-val" style={{ color: r.color || '#374151' }}>{r.value}</span>
          )}
        </div>
      ))}
    </div>
  </div>
);

/* ── SVG bar chart (Sales vs Purchases monthly) ──────────── */
const BarChart = ({ salesData, purchaseData, labels, onSalesBarClick, onPurchaseBarClick }) => {
  const W = 560, H = 140, PL = 38, PR = 10, PT = 10, PB = 20;
  const chartW = W - PL - PR;
  const chartH = H - PT - PB;
  const allVals = [...salesData, ...purchaseData].filter(v => v > 0);
  const maxVal  = Math.max(...allVals, 1);
  const cols    = labels.length;
  const slotW   = chartW / cols;
  const BW      = Math.min(14, slotW * 0.35);
  const ticks   = [0, 0.25, 0.5, 0.75, 1];

  /* hover tooltip state */
  const [tip, setTip] = useState(null); // { x, y, text }

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="dash-chart-svg"
      preserveAspectRatio="xMidYMid meet"
      onMouseLeave={() => setTip(null)}
    >
      {ticks.map((t, i) => {
        const y = PT + (1 - t) * chartH;
        return (
          <g key={i}>
            <line x1={PL} y1={y} x2={W - PR} y2={y} stroke="#f1f5f9" strokeWidth="1" />
            {t > 0 && (
              <text x={PL - 4} y={y + 3} fontSize="7" fill="#94a3b8" textAnchor="end">
                {fmt(maxVal * t)}
              </text>
            )}
          </g>
        );
      })}

      {labels.map((lbl, i) => {
        const cx = PL + i * slotW + slotW / 2;
        const sH = ((salesData[i] || 0) / maxVal) * chartH;
        const pH = ((purchaseData[i] || 0) / maxVal) * chartH;
        return (
          <g key={i}>
            {/* Sales bar */}
            <rect
              x={cx - BW - 1} y={PT + chartH - sH} width={BW} height={sH} rx="2"
              fill="#27445D"
              opacity={tip && tip.barKey === `s${i}` ? 1 : 0.85}
              style={{ cursor: salesData[i] > 0 ? 'pointer' : 'default' }}
              onMouseEnter={e => salesData[i] > 0 && setTip({
                x: cx - BW / 2 - 1,
                y: PT + chartH - sH - 4,
                text: `Sales: ₹${fmt(salesData[i])}`,
                barKey: `s${i}`,
                color: '#27445D',
              })}
              onClick={() => salesData[i] > 0 && onSalesBarClick && onSalesBarClick(i)}
            />
            {/* Purchase bar */}
            <rect
              x={cx + 1} y={PT + chartH - pH} width={BW} height={pH} rx="2"
              fill="#f5a623"
              opacity={tip && tip.barKey === `p${i}` ? 1 : 0.85}
              style={{ cursor: purchaseData[i] > 0 ? 'pointer' : 'default' }}
              onMouseEnter={e => purchaseData[i] > 0 && setTip({
                x: cx + BW / 2 + 1,
                y: PT + chartH - pH - 4,
                text: `Purchase: ₹${fmt(purchaseData[i])}`,
                barKey: `p${i}`,
                color: '#c47f00',
              })}
              onClick={() => purchaseData[i] > 0 && onPurchaseBarClick && onPurchaseBarClick(i)}
            />
            <text x={cx} y={H - 4} fontSize="7" fill="#94a3b8" textAnchor="middle">{lbl}</text>
          </g>
        );
      })}

      {/* Floating tooltip */}
      {tip && (() => {
        const TW = 80, TH = 14, TR = 3;
        const tx = Math.min(Math.max(tip.x - TW / 2, PL), W - PR - TW);
        const ty = Math.max(tip.y - TH - 2, PT);
        return (
          <g style={{ pointerEvents: 'none' }}>
            <rect x={tx} y={ty} width={TW} height={TH} rx={TR}
              fill={tip.color} opacity="0.92" />
            <text x={tx + TW / 2} y={ty + TH - 4} fontSize="6.5" fill="#fff"
              textAnchor="middle" fontWeight="600">
              {tip.text}
            </text>
          </g>
        );
      })()}
    </svg>
  );
};

/* ── Quick action button ─────────────────────────────────── */
const QBtn = ({ icon, label, to, onClick, color }) => {
  const inner = (
    <>
      <span className="dash-qbtn-icon" style={{ background: color }}>{icon}</span>
      <span className="dash-qbtn-label">{label}</span>
    </>
  );
  if (to) return <Link to={to} className="dash-qbtn">{inner}</Link>;
  return <button className="dash-qbtn" onClick={onClick}>{inner}</button>;
};

/* ── Spinner ─────────────────────────────────────────────── */
const Spinner = () => <div className="dash-spinner" />;

/* ══════════════════════════════════════════════════════════
   HOME / DASHBOARD
══════════════════════════════════════════════════════════ */
const Home = () => {
  const { isAuthenticated, companyId, branchId, permissions, token, logout, roleName } = useAuth();
  const hasReport = permissions?.includes('Read Reports');

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) logout();
  }, [isAuthenticated, token, logout]);

  /* ── date helpers ──────────────────────────────────────── */
  const [from] = useState(monthStart);
  const [to]   = useState(todayStr);

  /* ── KPI / DSS state ───────────────────────────────────── */
  const [kpi,     setKpi]     = useState(null);
  const [kpiLoad, setKpiLoad] = useState(false);

  const [todayDss,     setTodayDss]     = useState([]);
  const [todayLoad,    setTodayLoad]    = useState(false);

  /* monthly chart data */
  const [chartSales,    setChartSales]    = useState(Array(12).fill(0));
  const [chartPurchase, setChartPurchase] = useState(Array(12).fill(0));
  const [chartLoad,     setChartLoad]     = useState(false);

  /* purchase summary (MTD) */
  const [purData,    setPurData]    = useState([]);
  const [purLoad,    setPurLoad]    = useState(false);

  /* stock transfer */
  const [stockIn,   setStockIn]   = useState([]);
  const [stInLoad,  setStInLoad]  = useState(false);
  const [stockPend, setStockPend] = useState([]);
  const [stPendLoad,setStPendLoad]= useState(false);

  /* inter city stock transfer */
  const [ictStockIn,    setIctStockIn]    = useState([]);
  const [ictInLoad,     setIctInLoad]     = useState(false);
  const [ictStockPend,  setIctStockPend]  = useState([]);
  const [ictPendLoad,   setIctPendLoad]   = useState(false);

  /* stock value summary */
  const [stockValData,    setStockValData]    = useState([]);
  const [stockValLoad,    setStockValLoad]    = useState(false);

  /* ── monthly sales modal ──────────────────────────────────── */
  const [salesModal,     setSalesModal]     = useState(false);
  const [salesModalMonth,setSalesModalMonth]= useState(null); // 0-11
  const [salesModalData, setSalesModalData] = useState([]);
  const [salesModalLoad, setSalesModalLoad] = useState(false);

  const openSalesModal = useCallback(async (monthIndex) => {
    setSalesModal(true);
    setSalesModalMonth(monthIndex);
    setSalesModalData([]);
    setSalesModalLoad(true);
    try {
      const year  = new Date().getFullYear();
      const mm    = String(monthIndex + 1).padStart(2, '0');
      const lastDay = new Date(year, monthIndex + 1, 0).getDate();
      const fromD = `${year}-${mm}-01`;
      const toD   = `${year}-${mm}-${lastDay}`;
      const res = await fetchData(
        'ReportApi',
        `/DailySalesSummary/${companyId}/${branchId}/${fromD}/${toD}`,
        token
      );
      if (res.responseCode === '1111') setSalesModalData(res.response_data);
    } catch (e) { console.error(e); }
    finally { setSalesModalLoad(false); }
  }, [companyId, branchId, token]);

  /* ── monthly purchase modal ────────────────────────────────── */
  const [purModal,     setPurModal]     = useState(false);
  const [purModalMonth,setPurModalMonth]= useState(null);
  const [purModalData, setPurModalData] = useState([]);
  const [purModalLoad, setPurModalLoad] = useState(false);

  const openPurModal = useCallback(async (monthIndex) => {
    setPurModal(true);
    setPurModalMonth(monthIndex);
    setPurModalData([]);
    setPurModalLoad(true);
    try {
      const year    = new Date().getFullYear();
      const mm      = String(monthIndex + 1).padStart(2, '0');
      const lastDay = new Date(year, monthIndex + 1, 0).getDate();
      const fromD   = `${year}-${mm}-01`;
      const toD     = `${year}-${mm}-${lastDay}`;
      const res = await fetchData(
        'ReportApi',
        `/PurchaseSummary/${companyId}/${branchId}/${fromD}/${toD}`,
        token
      );
      if (res.responseCode === '1111') setPurModalData(res.response_data);
    } catch (e) { console.error(e); }
    finally { setPurModalLoad(false); }
  }, [companyId, branchId, token]);

  /* stock check */
  const [showStock,   setShowStock]   = useState(false);
  const [stockItem,   setStockItem]   = useState(null);
  const [itemId,      setItemId]      = useState('');
  const [branchStock, setBranchStock] = useState([]);
  const [stockLoad,   setStockLoad]   = useState(false);

  /* ── fetch KPI (month-to-date) ─────────────────────────── */
  const fetchKpi = useCallback(async () => {
    if (!hasReport) return;
    setKpiLoad(true);
    try {
      const res = await fetchData('ReportApi', `/KPIReport/${companyId}/${branchId}/${from}/${to}`, token);
      if (res.responseCode === '1111') {
        const rows = res.response_data;
        const netSales = rows.reduce((s, r) => s + r.NetSales, 0);
        const pcs      = rows.reduce((s, r) => s + r.NumberofPcs, 0);
        const bills    = rows.reduce((s, r) => s + r.NumberofBills, 0);
        const single   = rows.reduce((s, r) => s + r.NumberofSingleBills, 0);
        setKpi({
          netSales,
          pcs,
          bills,
          asp      : pcs   > 0 ? netSales / pcs   : 0,
          vpt      : bills > 0 ? netSales / bills  : 0,
          upt      : bills > 0 ? pcs      / bills  : 0,
          singlePct: bills > 0 ? (single * 100) / bills : 0,
        });
      }
    } catch (e) { console.error(e); }
    finally { setKpiLoad(false); }
  }, [companyId, branchId, from, to, token, hasReport]);

  /* ── fetch today's DSS ─────────────────────────────────── */
  const fetchToday = useCallback(async () => {
    if (!hasReport) return;
    setTodayLoad(true);
    try {
      const t = todayStr();
      const res = await fetchData('ReportApi', `/DailySalesSummary/${companyId}/${branchId}/${t}/${t}`, token);
      if (res.responseCode === '1111') setTodayDss(res.response_data);
    } catch (e) { console.error(e); }
    finally { setTodayLoad(false); }
  }, [companyId, branchId, token, hasReport]);

  /* ── fetch monthly chart data (12 months) ──────────────── */
  const fetchChart = useCallback(async () => {
    if (!hasReport) return;
    setChartLoad(true);
    try {
      const year = new Date().getFullYear();
      const salesArr    = Array(12).fill(0);
      const purchaseArr = Array(12).fill(0);

      /* sales — DailySalesSummary full year */
      const salesRes = await fetchData(
        'ReportApi',
        `/DailySalesSummary/${companyId}/${branchId}/${year}-01-01/${year}-12-31`,
        token
      );
      if (salesRes.responseCode === '1111') {
        salesRes.response_data.forEach(r => {
          const m = new Date(r.BillDate).getMonth();
          salesArr[m] += r.TotalAmount;
        });
      }

      /* purchases — PurchaseSummary full year, field: GRNDate + TotalAmount */
      const purRes = await fetchData(
        'ReportApi',
        `/PurchaseSummary/${companyId}/${branchId}/${year}-01-01/${year}-12-31`,
        token
      );
      if (purRes.responseCode === '1111') {
        purRes.response_data.forEach(r => {
          try {
            const m = new Date(r.GRNDate).getMonth();
            if (m >= 0 && m <= 11) purchaseArr[m] += (r.TotalAmount || 0);
          } catch { /* skip bad dates */ }
        });
      }

      setChartSales(salesArr);
      setChartPurchase(purchaseArr);
    } catch (e) { console.error(e); }
    finally { setChartLoad(false); }
  }, [companyId, branchId, token, hasReport]);

  /* ── fetch purchase summary MTD ────────────────────────── */
  const fetchPurchase = useCallback(async () => {
    if (!hasReport) return;
    setPurLoad(true);
    try {
      const res = await fetchData(
        'ReportApi',
        `/PurchaseSummary/${companyId}/${branchId}/${from}/${to}`,
        token
      );
      if (res.responseCode === '1111') setPurData(res.response_data);
    } catch (e) { console.error(e); }
    finally { setPurLoad(false); }
  }, [companyId, branchId, from, to, token, hasReport]);

  /* ── stock transfer in ─────────────────────────────────── */
  const fetchStockIn = useCallback(async () => {
    setStInLoad(true);
    try {
      const res = await fetchData('ReportApi', `/Stockin/${companyId}/${branchId}`, token);
      if (res.responseCode === '1111') setStockIn(res.response_data);
    } catch (e) { console.error(e); }
    finally { setStInLoad(false); }
  }, [companyId, branchId, token]);

  /* ── stock transfer pending ────────────────────────────── */
  const fetchStockPend = useCallback(async () => {
    setStPendLoad(true);
    try {
      const res = await fetchData('ReportApi', `/Stockinpend/${companyId}/${branchId}`, token);
      if (res.responseCode === '1111') setStockPend(res.response_data);
    } catch (e) { console.error(e); }
    finally { setStPendLoad(false); }
  }, [companyId, branchId, token]);

  /* ── inter city stock transfer in ─────────────────────── */
  const fetchIctStockIn = useCallback(async () => {
    setIctInLoad(true);
    try {
      const res = await fetchData('ReportApi', `/InterCityStockin/${companyId}/${branchId}`, token);
      if (res.responseCode === '1111') setIctStockIn(res.response_data);
    } catch (e) { console.error(e); }
    finally { setIctInLoad(false); }
  }, [companyId, branchId, token]);

  /* ── inter city stock transfer pending ────────────────── */
  const fetchIctStockPend = useCallback(async () => {
    setIctPendLoad(true);
    try {
      const res = await fetchData('ReportApi', `/InterCityStockinpend/${companyId}/${branchId}`, token);
      if (res.responseCode === '1111') setIctStockPend(res.response_data);
    } catch (e) { console.error(e); }
    finally { setIctPendLoad(false); }
  }, [companyId, branchId, token]);

  /* ── fetch stock value summary ─────────────────────────── */
  const fetchStockValue = useCallback(async () => {
    if (!hasReport) return;
    setStockValLoad(true);
    try {
      const res = await fetchData('ReportApi', `/Stock/${companyId}/${branchId}/${from}/${to}`, token);
      if (res.responseCode === '1111') setStockValData(res.response_data);
    } catch (e) { console.error(e); }
    finally { setStockValLoad(false); }
  }, [companyId, branchId, from, to, token, hasReport]);

  /* ── initial load ──────────────────────────────────────── */
  useEffect(() => {
    fetchKpi();
    fetchToday();
    fetchChart();
    fetchPurchase();
    fetchStockIn();
    fetchStockPend();
    fetchStockValue();
    fetchIctStockIn();
    fetchIctStockPend();
  }, [fetchKpi, fetchToday, fetchChart, fetchPurchase, fetchStockIn, fetchStockPend, fetchStockValue, fetchIctStockIn, fetchIctStockPend]);

  /* ── stock check ───────────────────────────────────────── */
  const loadOptions = async (input) => {
    if (input.length <= 4) return [];
    try {
      const res = await fetchData('PurchaseApi', `/Item/${companyId}/${branchId}/${input}`, token);
      if (res.responseCode === '1111')
        return res.response_data.map(i => ({ value: i.Id, label: `${i.Code} – ${i.Name}` }));
    } catch (e) { console.error(e); }
    return [];
  };

  const handleStockSearch = async () => {
    if (!itemId) return;
    setStockLoad(true);
    try {
      const res = await fetchData('ReportApi', `/BranchStock/${companyId}/${itemId}`, token);
      if (res.responseCode === '1111') setBranchStock(res.response_data);
    } catch (e) { console.error(e); }
    finally { setStockLoad(false); }
  };

  /* ── derived today totals ──────────────────────────────── */
  const todayBillSet = new Set(todayDss.map(r => r.BillNumber));
  const todayBills   = todayBillSet.size;
  const todayAmt     = todayDss.reduce((s, r) => s + r.TotalAmount, 0);
  const todayQty     = todayDss.reduce((s, r) => s + r.Quantity, 0);

  /* ── derived purchase totals (MTD) ─────────────────────── */
  const purBillSet = new Set(purData.map(r => r.GRNNumber));
  const purBills   = purBillSet.size;
  const purAmt     = purData.reduce((s, r) => s + (r.TotalAmount || 0), 0);
  const purQty     = purData.reduce((s, r) => s + (r.Quantity    || 0), 0);

  /* today's purchases — compare date portion only */
  const todayIso   = todayStr();
  const todayPur    = purData.filter(r => {
    try { return new Date(r.GRNDate).toISOString().split('T')[0] === todayIso; }
    catch { return false; }
  });
  const todayPurAmt = todayPur.reduce((s, r) => s + (r.TotalAmount || 0), 0);

  /* ── derived stock value totals ────────────────────────── */
  const totalSaleValue   = stockValData.reduce((s, r) => s + (r.SaleValue  || 0), 0);
  const totalClosingQty  = stockValData.reduce((s, r) => s + (r.ClosingQty || 0), 0);

  const allowedEcomm = [2, 1004, 1010];

  /* ════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════ */
  return (
    <div className="home-container dash-root">

      {/* ══ TOP SUMMARY CARDS ROW ══════════════════════════ */}
      <div className="dash-sum-row-grid">
        <SummaryCard
          icon={<TrendingUpIcon />} color="#27445D"
          label="Revenue (Month)" to="/DailySalesSummary"
          value={`₹ ${fmt(kpi?.netSales)}`} loading={kpiLoad}
          rows={[
            { label: "Today's Sales", value: `₹ ${fmt(todayAmt)}`, color: '#16a34a' },
            { label: 'Total Bills',   value: fmt(kpi?.bills) },
            { label: 'Avg Bill',      value: `₹ ${fmt(kpi?.vpt)}` },
          ]}
        />
        <SummaryCard
          icon={<AddShoppingCartIcon />} color="#7c3aed"
          label="Purchases (Month)" to="/PurchaseIndex"
          value={`₹ ${fmt(purAmt)}`} loading={purLoad}
          rows={[
            { label: "Today's Purchase", value: `₹ ${fmt(todayPurAmt)}`, color: '#16a34a' },
            { label: 'Total Bills',      value: purBills },
            { label: 'Total Qty',        value: fmt(purQty) },
          ]}
        />
        <SummaryCard
          icon={<Inventory2Icon />} color="#0891b2"
          label="Stock Value" to="/Stock"
          value={`₹ ${fmt(totalSaleValue)}`} loading={stockValLoad}
          rows={[
            { label: 'Closing Qty', value: fmt(totalClosingQty) },
            { label: 'Sale Value',  value: `₹ ${fmt(totalSaleValue)}` },
          ]}
        />
        <SummaryCard
          icon={<AssessmentIcon />} color="#059669"
          label="KPI (Month)" to="/KPIReport"
          value={`₹ ${fmt(kpi?.netSales)}`} loading={kpiLoad}
          rows={[
            { label: 'ASP',  value: `₹ ${fmt(kpi?.asp)}` },
            { label: 'UPT',  value: kpi ? kpi.upt.toFixed(2) : '—' },
            { label: 'VPT',  value: `₹ ${fmt(kpi?.vpt)}` },
          ]}
        />
        <SummaryCard
          icon={<SwapHorizIcon />} color="#d97706"
          label="Stock Transfers"
          value={stockIn.length + stockPend.length}
          loading={stInLoad || stPendLoad}
          rows={[
            { label: 'Pending In',       value: stockIn.length,   color: stockIn.length   > 0 ? '#dc2626' : '#374151' },
            { label: 'Pending Approval', value: stockPend.length, color: stockPend.length > 0 ? '#d97706' : '#374151' },
          ]}
        />
        <SummaryCard
          icon={<PeopleAltIcon />} color="#db2777"
          label="Today Snapshot"
          value={`₹ ${fmt(todayAmt)}`} loading={todayLoad}
          rows={[
            { label: 'Bills',  value: todayBills },
            { label: 'Pieces', value: todayQty },
            { label: 'Single Bill %', value: kpi ? `${kpi.singlePct.toFixed(1)}%` : '—' },
          ]}
        />
      </div>

      {/* ══ MAIN GRID (left + right) ════════════════════════ */}
      <div className="dash-main-grid">

        {/* ── LEFT COLUMN ─────────────────────────────────── */}
        <div className="dash-left-col">

          {/* Quick Actions */}
          <div className="dash-panel">
            <div className="dash-panel-title">
              <AddIcon style={{ fontSize: 14 }} /> Quick Actions
            </div>
            <div className="dash-qbtn-grid">
              {roleName !== 'Accounts' && (<>
                <QBtn icon={<ShoppingCartIcon style={{ fontSize: 14 }} />}    label="New Sale"       to="/SaleCreate"                    color="#27445D" />
                <QBtn icon={<AddShoppingCartIcon style={{ fontSize: 14 }} />} label="New Purchase"   to="/PurchaseCreate"                 color="#7c3aed" />
                <QBtn icon={<BuildIcon style={{ fontSize: 14 }} />}           label="Repair / Repl." to="/RepairAndReplacementCreate"     color="#059669" />
                <QBtn icon={<ExitToAppIcon style={{ fontSize: 14 }} />}       label="Stock Out"      to="/StockTransferCreate"            color="#0891b2" />
                {allowedEcomm.includes(Number(branchId)) && (
                  <QBtn icon={<ExitToAppIcon style={{ fontSize: 14 }} />}     label="Stock Out Ecomm" to="/StockTransferEcommCreate"     color="#428C42" />
                )}
                <QBtn icon={<SwapHorizIcon style={{ fontSize: 14 }} />}       label="Intercity Transfer" to="/InterCityStockTransferCreate" color="#7b1fa2" />
                <QBtn icon={<InventoryIcon style={{ fontSize: 14 }} />}       label="Stock Report"   to="/Stock"                         color="#d97706" />
                <QBtn icon={<AssessmentIcon style={{ fontSize: 14 }} />}      label="DSS"            to="/DailySalesSummary"             color="#8B0000" />
                <QBtn icon={<ReceiptLongIcon style={{ fontSize: 14 }} />}     label="KPI Report"     to="/KPIReport"                     color="#db2777" />
              </>)}
              <QBtn icon={<VisibilityIcon style={{ fontSize: 14 }} />} label="Stock Check"
                onClick={() => { setShowStock(v => !v); setBranchStock([]); setStockItem(null); }}
                color="#483D8B" />
            </div>
          </div>

          {/* Stock Check */}
          {showStock && (
            <div className="dash-panel">
              <div className="dash-panel-title"><VisibilityIcon style={{ fontSize: 14 }} /> Stock Check</div>
              <div className="dash-stock-row">
                <div className="dash-stock-select">
                  <label className="home-label">Item (type 5+ chars)</label>
                  <AsyncSelect
                    cacheOptions loadOptions={loadOptions}
                    value={stockItem}
                    onChange={opt => { setStockItem(opt); setItemId(opt ? opt.value : ''); }}
                    menuPortalTarget={document.body}
                    placeholder="Search item…"
                    styles={{
                      control: (b, s) => ({ ...b, borderRadius: 7, borderColor: s.isFocused ? '#63cab7' : '#d1d5db', minHeight: 30, height: 30, fontSize: 12 }),
                      menuPortal: b => ({ ...b, zIndex: 9999 }),
                      menu: b => ({ ...b, fontSize: 12 }),
                      option: b => ({ ...b, fontSize: 12, padding: '5px 10px' }),
                      singleValue: b => ({ ...b, fontSize: 12 }),
                      valueContainer: b => ({ ...b, padding: '0 8px' }),
                      indicatorsContainer: b => ({ ...b, height: 30 }),
                    }}
                  />
                </div>
                <button className="home-btn" onClick={handleStockSearch}
                  style={{ background: 'linear-gradient(135deg,#63cab7,#2c9e8a)', marginTop: 18, padding: '5px 12px' }}>
                  <RefreshIcon style={{ fontSize: 13 }} /> Search
                </button>
              </div>
              {stockLoad && <Spinner />}
              {!stockLoad && branchStock.length > 0 && (
                <div className="home-table-wrap" style={{ marginTop: 8 }}>
                  <table>
                    <thead><tr><th>Branch</th><th style={{ textAlign: 'right' }}>Qty</th></tr></thead>
                    <tbody>
                      {branchStock.map((r, i) => (
                        <tr key={i}><td>{r.BranchName}</td><td style={{ textAlign: 'right' }}>{r.ClosingQty.toFixed(2)}</td></tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>Total</td>
                        <td style={{ textAlign: 'right' }}>{branchStock.reduce((s, r) => s + r.ClosingQty, 0).toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Stock Transfer In */}
          {roleName !== 'Accounts' && (
            <div className="dash-panel">
              <div className="dash-panel-title" style={{ justifyContent: 'space-between' }}>
                <span><SwapHorizIcon style={{ fontSize: 14 }} /> Confirm Stock Transfer In
                  {stockIn.length > 0 && <span className="dash-badge">{stockIn.length}</span>}
                </span>
                <button className="dash-refresh-icon" onClick={fetchStockIn}><RefreshIcon style={{ fontSize: 13 }} /></button>
              </div>
              {stInLoad ? <Spinner /> : (
                stockIn.length === 0
                  ? <div className="dash-empty">No pending transfers</div>
                  : <div className="home-table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th><th>Bill #</th><th>From Branch</th>
                            <th className="col-hide-phone" style={{ textAlign: 'right' }}>Amount</th>
                            <th style={{ textAlign: 'right' }}>Qty</th>
                            <th style={{ textAlign: 'center' }}>Act</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stockIn.map(item => (
                            <tr key={item.Id} className={item.InputAccepted ? 'row-accepted' : 'row-pending'}>
                              <td>{fmtD(item.BillDate)}</td>
                              <td>{item.BillPrefix}/S/{item.BillNumber}</td>
                              <td>{item.ToBranchName}</td>
                              <td className="col-hide-phone" style={{ textAlign: 'right' }}>₹{fmt(item.GrandTotal)}</td>
                              <td style={{ textAlign: 'right' }}>{item.TotalQuantity}</td>
                              <td style={{ textAlign: 'center' }}>
                                <IconButton component={Link} to={`/StockInDetail/${item.Id}`} size="small">
                                  <InfoIcon style={{ fontSize: 15, color: 'goldenrod' }} />
                                </IconButton>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
              )}
            </div>
          )}

          {/* Stock Transfer Pending */}
          {roleName !== 'Accounts' && (
            <div className="dash-panel">
              <div className="dash-panel-title" style={{ justifyContent: 'space-between' }}>
                <span><SwapHorizIcon style={{ fontSize: 14 }} /> Approval Pending
                  {stockPend.length > 0 && <span className="dash-badge dash-badge-warn">{stockPend.length}</span>}
                </span>
                <button className="dash-refresh-icon" onClick={fetchStockPend}><RefreshIcon style={{ fontSize: 13 }} /></button>
              </div>
              {stPendLoad ? <Spinner /> : (
                stockPend.length === 0
                  ? <div className="dash-empty">No pending approvals</div>
                  : <div className="home-table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th><th>Bill #</th><th>To Branch</th>
                            <th className="col-hide-phone" style={{ textAlign: 'right' }}>Amount</th>
                            <th style={{ textAlign: 'right' }}>Qty</th>
                            <th style={{ textAlign: 'center' }}>Act</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stockPend.map(item => (
                            <tr key={item.Id} className={item.InputAccepted ? 'row-accepted' : 'row-pending'}>
                              <td>{fmtD(item.BillDate)}</td>
                              <td>{item.BillPrefix}/S/{item.BillNumber}</td>
                              <td>{item.ToBranchName}</td>
                              <td className="col-hide-phone" style={{ textAlign: 'right' }}>₹{fmt(item.GrandTotal)}</td>
                              <td style={{ textAlign: 'right' }}>{item.TotalQuantity}</td>
                              <td style={{ textAlign: 'center' }}>
                                <IconButton component={Link} to={`/StockInPending/${item.Id}`} size="small">
                                  <InfoIcon style={{ fontSize: 15, color: 'goldenrod' }} />
                                </IconButton>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
              )}
            </div>
          )}

          {/* Inter City Stock Transfer In */}
          {roleName !== 'Accounts' && (
            <div className="dash-panel">
              <div className="dash-panel-title" style={{ justifyContent: 'space-between' }}>
                <span><SwapHorizIcon style={{ fontSize: 14 }} /> Confirm Inter City Stock Transfer In
                  {ictStockIn.length > 0 && <span className="dash-badge">{ictStockIn.length}</span>}
                </span>
                <button className="dash-refresh-icon" onClick={fetchIctStockIn}><RefreshIcon style={{ fontSize: 13 }} /></button>
              </div>
              {ictInLoad ? <Spinner /> : (
                ictStockIn.length === 0
                  ? <div className="dash-empty">No pending transfers</div>
                  : <div className="home-table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th><th>Bill #</th><th>From Branch</th>
                            <th className="col-hide-phone" style={{ textAlign: 'right' }}>Amount</th>
                            <th style={{ textAlign: 'right' }}>Qty</th>
                            <th style={{ textAlign: 'center' }}>Act</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ictStockIn.map(item => (
                            <tr key={item.Id} className={item.InputAccepted ? 'row-accepted' : 'row-pending'}>
                              <td>{fmtD(item.BillDate)}</td>
                              <td>{item.BillPrefix}/S/{item.BillNumber}</td>
                              <td>{item.ToBranchName}</td>
                              <td className="col-hide-phone" style={{ textAlign: 'right' }}>₹{fmt(item.GrandTotal)}</td>
                              <td style={{ textAlign: 'right' }}>{item.TotalQuantity}</td>
                              <td style={{ textAlign: 'center' }}>
                                <IconButton component={Link} to={`/InterCityStockInDetail/${item.Id}`} size="small">
                                  <InfoIcon style={{ fontSize: 15, color: 'goldenrod' }} />
                                </IconButton>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
              )}
            </div>
          )}

          {/* Inter City Stock Transfer Approval Pending */}
          {roleName !== 'Accounts' && (
            <div className="dash-panel">
              <div className="dash-panel-title" style={{ justifyContent: 'space-between' }}>
                <span><SwapHorizIcon style={{ fontSize: 14 }} /> Inter City Approval Pending
                  {ictStockPend.length > 0 && <span className="dash-badge dash-badge-warn">{ictStockPend.length}</span>}
                </span>
                <button className="dash-refresh-icon" onClick={fetchIctStockPend}><RefreshIcon style={{ fontSize: 13 }} /></button>
              </div>
              {ictPendLoad ? <Spinner /> : (
                ictStockPend.length === 0
                  ? <div className="dash-empty">No pending approvals</div>
                  : <div className="home-table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th><th>Bill #</th><th>To Branch</th>
                            <th className="col-hide-phone" style={{ textAlign: 'right' }}>Amount</th>
                            <th style={{ textAlign: 'right' }}>Qty</th>
                            <th style={{ textAlign: 'center' }}>Act</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ictStockPend.map(item => (
                            <tr key={item.Id} className={item.InputAccepted ? 'row-accepted' : 'row-pending'}>
                              <td>{fmtD(item.BillDate)}</td>
                              <td>{item.BillPrefix}/S/{item.BillNumber}</td>
                              <td>{item.ToBranchName}</td>
                              <td className="col-hide-phone" style={{ textAlign: 'right' }}>₹{fmt(item.GrandTotal)}</td>
                              <td style={{ textAlign: 'right' }}>{item.TotalQuantity}</td>
                              <td style={{ textAlign: 'center' }}>
                                <IconButton component={Link} to={`/InterCityStockInPending/${item.Id}`} size="small">
                                  <InfoIcon style={{ fontSize: 15, color: 'goldenrod' }} />
                                </IconButton>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
              )}
            </div>
          )}
        </div>

        {/* ── RIGHT COLUMN ────────────────────────────────── */}
        <div className="dash-right-col">

          {/* Sales vs Purchases chart */}
          {hasReport && (
            <div className="dash-panel">
              <div className="dash-panel-title" style={{ justifyContent: 'space-between' }}>
                <span>Sales vs Purchases (Monthly)</span>
                <span className="dash-chart-legend">
                  <span className="dash-legend-dot" style={{ background: '#27445D' }} /> Sales
                  <span className="dash-legend-dot" style={{ background: '#f5a623', marginLeft: 8 }} /> Purchases
                </span>
              </div>
              {chartLoad
                ? <Spinner />
                : <BarChart salesData={chartSales} purchaseData={chartPurchase} labels={MONTHS} onSalesBarClick={openSalesModal} onPurchaseBarClick={openPurModal} />
              }
            </div>
          )}

          {/* Today's sales detail */}
          {hasReport && (
            <div className="dash-panel">
              <div className="dash-panel-title" style={{ justifyContent: 'space-between' }}>
                <span><ReceiptLongIcon style={{ fontSize: 14 }} /> Today's Sales
                  <span className="dash-badge" style={{ background: '#27445D' }}>{todayBills} bills</span>
                </span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button className="dash-refresh-icon" onClick={fetchToday}><RefreshIcon style={{ fontSize: 13 }} /></button>
                  <Link to="/DailySalesSummary" className="dash-view-all">Full Report →</Link>
                </div>
              </div>
              {todayLoad ? <Spinner /> : (
                <div className="home-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Bill #</th>
                        <th>Item Code</th>
                        <th>Item</th>
                        <th className="col-hide-phone">Category</th>
                        <th style={{ textAlign: 'right' }}>Qty</th>
                        <th style={{ textAlign: 'right' }}>Amount</th>
                        <th className="col-hide-tablet">Payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {todayDss.length === 0
                        ? <tr><td colSpan={7} className="dash-empty">No sales today</td></tr>
                        : todayDss.slice(0, 25).map((r, i) => (
                          <tr key={i}>
                            <td>{r.BillNumber}</td>
                            <td>{r.Code}</td>
                            <td>{r.ItemName}</td>
                            <td className="col-hide-phone">{r.CategoryName}</td>
                            <td style={{ textAlign: 'right' }}>{r.Quantity}</td>
                            <td style={{ textAlign: 'right' }}>₹{fmt(r.TotalAmount)}</td>
                            <td className="col-hide-tablet">{r.ModeOfPayment}</td>
                          </tr>
                        ))
                      }
                    </tbody>
                    {todayDss.length > 0 && (
                      <tfoot>
                        <tr>
                          <td colSpan={4}>Total ({todayBills} bills)</td>
                          <td style={{ textAlign: 'right' }}>{todayQty}</td>
                          <td style={{ textAlign: 'right' }}>₹{fmt(todayAmt)}</td>
                          <td className="col-hide-tablet" />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>
          )}

          {/* KPI summary panel */}
          {hasReport && (
            <div className="dash-panel">
              <div className="dash-panel-title" style={{ justifyContent: 'space-between' }}>
                <span><AssessmentIcon style={{ fontSize: 14 }} /> KPI Summary (MTD)</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button className="dash-refresh-icon" onClick={fetchKpi}><RefreshIcon style={{ fontSize: 13 }} /></button>
                  <Link to="/KPIReport" className="dash-view-all">Full Report →</Link>
                </div>
              </div>
              <div className="dash-kpi-mini-grid">
                {[
                  { label: 'Net Sales',    value: `₹ ${fmt(kpi?.netSales)}`,              color: '#27445D' },
                  { label: 'Bills',        value: fmt(kpi?.bills),                         color: '#7c3aed' },
                  { label: 'Pieces',       value: fmt(kpi?.pcs),                           color: '#d97706' },
                  { label: 'ASP',          value: `₹ ${fmt(kpi?.asp)}`,                   color: '#059669' },
                  { label: 'VPT',          value: `₹ ${fmt(kpi?.vpt)}`,                   color: '#0891b2' },
                  { label: 'UPT',          value: kpi ? kpi.upt.toFixed(2) : '—',         color: '#db2777' },
                  { label: 'Single Bill%', value: kpi ? `${kpi.singlePct.toFixed(1)}%` : '—', color: '#8B0000' },
                ].map((item, i) => (
                  <div key={i} className="dash-kpi-mini" style={{ '--mc': item.color }}>
                    <div className="dash-kpi-mini-label">{item.label}</div>
                    {kpiLoad ? <Skel /> : <div className="dash-kpi-mini-value">{item.value}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      <div className="home-watermark">
        <CopyrightIcon style={{ fontSize: 12 }} />
        <b>EFFTABS SOLUTIONS LLP</b>
      </div>

      {/* ══ MONTHLY SALES MODAL ════════════════════════════ */}
      {salesModal && (
        <div className="dash-modal-overlay" onClick={() => setSalesModal(false)}>
          <div className="dash-modal" onClick={e => e.stopPropagation()}>
            <div className="dash-modal-header">
              <span>
                <ReceiptLongIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 5 }} />
                Sales Details — {salesModalMonth !== null ? MONTHS[salesModalMonth] : ''} {new Date().getFullYear()}
              </span>
              <button className="dash-modal-close" onClick={() => setSalesModal(false)}>✕</button>
            </div>
            <div className="dash-modal-body">
              {salesModalLoad ? <Spinner /> : (
                <>
                  {salesModalData.length === 0
                    ? <div className="dash-empty">No sales data for this month.</div>
                    : (() => {
                        const billSet = new Set(salesModalData.map(r => r.BillNumber));
                        const totalAmt = salesModalData.reduce((s, r) => s + r.TotalAmount, 0);
                        const totalQty = salesModalData.reduce((s, r) => s + r.Quantity, 0);
                        return (
                          <div className="home-table-wrap">
                            <table>
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Bill #</th>
                                  <th>Item Code</th>
                                  <th>Item</th>
                                  <th className="col-hide-phone">Category</th>
                                  <th style={{ textAlign: 'right' }}>Qty</th>
                                  <th style={{ textAlign: 'right' }}>Amount</th>
                                  <th className="col-hide-tablet">Payment</th>
                                </tr>
                              </thead>
                              <tbody>
                                {salesModalData.map((r, i) => (
                                  <tr key={i}>
                                    <td>{fmtD(r.BillDate)}</td>
                                    <td>{r.BillNumber}</td>
                                    <td>{r.Code}</td>
                                    <td>{r.ItemName}</td>
                                    <td className="col-hide-phone">{r.CategoryName}</td>
                                    <td style={{ textAlign: 'right' }}>{r.Quantity}</td>
                                    <td style={{ textAlign: 'right' }}>₹{fmt(r.TotalAmount)}</td>
                                    <td className="col-hide-tablet">{r.ModeOfPayment}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td colSpan={5}>Total ({billSet.size} bills)</td>
                                  <td style={{ textAlign: 'right' }}>{totalQty}</td>
                                  <td style={{ textAlign: 'right' }}>₹{fmt(totalAmt)}</td>
                                  <td className="col-hide-tablet" />
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        );
                      })()
                  }
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ══ MONTHLY PURCHASE MODAL ═════════════════════════ */}
      {purModal && (
        <div className="dash-modal-overlay" onClick={() => setPurModal(false)}>
          <div className="dash-modal" onClick={e => e.stopPropagation()}>
            <div className="dash-modal-header dash-modal-header--purchase">
              <span>
                <AddShoppingCartIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 5 }} />
                Purchase Details — {purModalMonth !== null ? MONTHS[purModalMonth] : ''} {new Date().getFullYear()}
              </span>
              <button className="dash-modal-close" onClick={() => setPurModal(false)}>✕</button>
            </div>
            <div className="dash-modal-body">
              {purModalLoad ? <Spinner /> : (
                <>
                  {purModalData.length === 0
                    ? <div className="dash-empty">No purchase data for this month.</div>
                    : (() => {
                        const billSet  = new Set(purModalData.map(r => r.GRNNumber));
                        const totalAmt = purModalData.reduce((s, r) => s + (r.TotalAmount || 0), 0);
                        const totalQty = purModalData.reduce((s, r) => s + (r.Quantity    || 0), 0);
                        return (
                          <div className="home-table-wrap">
                            <table>
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>GRN #</th>
                                  <th>Item Code</th>
                                  <th>Item</th>
                                  <th className="col-hide-phone">Category</th>
                                  <th style={{ textAlign: 'right' }}>Qty</th>
                                  <th style={{ textAlign: 'right' }}>Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {purModalData.map((r, i) => (
                                  <tr key={i}>
                                    <td>{fmtD(r.GRNDate)}</td>
                                    <td>{r.GRNNumber}</td>
                                    <td>{r.Code}</td>
                                    <td>{r.ItemName}</td>
                                    <td className="col-hide-phone">{r.CategoryName}</td>
                                    <td style={{ textAlign: 'right' }}>{r.Quantity}</td>
                                    <td style={{ textAlign: 'right' }}>₹{fmt(r.TotalAmount)}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td colSpan={5}>Total ({billSet.size} GRNs)</td>
                                  <td style={{ textAlign: 'right' }}>{totalQty}</td>
                                  <td style={{ textAlign: 'right' }}>₹{fmt(totalAmt)}</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        );
                      })()
                  }
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
